import { Outlet } from 'react-router-dom'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import type { Back } from 'modules/core/components/Card/types'
import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import dayConfigurations, {
  DayConfigurationsProvider,
} from 'modules/work_schedules/store/collections/dayConfigurations'
import overlapPeriods from 'modules/work_schedules/store/collections/overlapPeriods'
import schedules from 'modules/work_schedules/store/collections/schedules'

const WorkScheduleRootPage = () => {
  const { navigation } = useNavigation()

  const { id } = useStrictParams<'id'>()

  const back: Back = {
    href: navigation.settings.workSchedule.fullPath(),
    label: i18n.t('settings.time_management.tab'),
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { loading } = useAsyncData(
    () =>
      Promise.all([
        schedules.fetch({ data: { id: Number(id) } }),
        overlapPeriods.fetch({ data: { schedule_id: Number(id) } }),
        dayConfigurations.fetch({ data: { schedule_id: Number(id) } }),
      ]),
    [id]
  )

  const workSchedule = schedules?.get(Number(id))
  const workScheduleId = workSchedule?.attributes.id
  const defaultPeriod = workScheduleId ? overlapPeriods.getDefaultPeriod(workScheduleId) : undefined

  if (loading) return <Loading />
  if (!workSchedule) return <NotFound />

  return (
    <AuthorizeUser
      authorizeFn={() => users.current.policy.can('workSchedule.manageCompanySchedules')}
    >
      <DayConfigurationsProvider collection={dayConfigurations}>
        <Outlet
          context={{
            isNotFound: !workSchedule.id,
            back,
            workSchedule,
            overlapPeriods,
            defaultPeriod,
            dayConfigurations,
            loading,
          }}
        />
      </DayConfigurationsProvider>
    </AuthorizeUser>
  )
}

export default WorkScheduleRootPage
